import React from "react";
import { FeatureFlags } from "us.common/constants";
import { useFeatureFlag } from "us.common/FeatureFlagContext";
import { Loader, LottieAnimation } from "us.common/components";
import { HomeAnimation } from "us.common/components/animations";
import { useTranslation } from "react-i18next";

import "./WithFeatureFlag.scss";

type FeatureFlagProps = {
  flag: FeatureFlags;
  children: JSX.Element;
  loader?: JSX.Element;
  fallback?: JSX.Element;
};

/**
 * @description - Component to get feature flags.
 * @link - https://unicorn-solutions.atlassian.net/wiki/spaces/BT/pages/3775037443/How+to+use+feature+flags
 * @author Tharanga Niroshana
 * @since 27/11/2024
 */

const WithFeatureFlag = ({
  flag,
  children,
  loader,
  fallback,
}: FeatureFlagProps) => {
  const { isEnabled, isLoading } = useFeatureFlag(flag); // Assume useFeatureFlag provides a loading state
  const { t } = useTranslation();

  if (isLoading) {
    return loader ?? <Loader isDescriptionAvailable={false} />;
  }

  if (isEnabled) {
    return children;
  }

  return (
    fallback ?? (
      <div className="feature-wrap">
        <LottieAnimation lotti={HomeAnimation} height={362} width={675} />
        <h1
          className="text-primary font-weight-bold mb-1"
          style={{ fontSize: "1.5rem" }}
        >
          {t(
            "US.COMMON:COMMON.THIS_FEATURE_IS_NOT_AVAILABLE_UNDER_YOUR_SUBSCRIPTION"
          )}
        </h1>
        <p className="text-muted font-lg">
          {t(
            "US.COMMON:COMMON.PLEASE_CONTACT_PRODUCT_MANAGEMENT_TEAM_FOR_MORE_INFORMATION"
          )}
        </p>
      </div>
    )
  );
};

export default WithFeatureFlag;
